import { ChevronLeft, ChevronRight } from 'lucide-react';

import { DimeButton } from './DimeButton.js';
import { DimeButtonGroup } from './DimeButtonGroup.js';
import { DimePagination } from './DimePagination.js';
import { cn } from './lib/utils.js';

type DimePagerProps = Readonly<{
  /** Zero-based position of the item shown. */
  index: number;
  count: number;
  onIndexChange: (index: number) => void;
  /** Names the pager, e.g. "Similar customers". */
  label: string;
  /** One item, for the buttons' names, e.g. "customer" gives "Previous customer". */
  itemName: string;
  className?: string;
}>;

/** Steps through items shown one at a time, "‹ 1 of 3 ›"; renders nothing for a single item. */
export function DimePager({
  index,
  count,
  onIndexChange,
  label,
  itemName,
  className,
}: DimePagerProps) {
  if (count < 2) return null;
  return (
    <DimePagination aria-label={label} className={cn('mx-0 w-auto items-center gap-2', className)}>
      <span
        aria-live="polite"
        aria-atomic="true"
        className="text-sm text-muted-foreground tabular-nums"
      >
        {index + 1} of {count}
      </span>
      <DimeButtonGroup>
        <DimeButton
          type="button"
          variant="outline"
          size="icon-sm"
          disabled={index === 0}
          onClick={() => onIndexChange(index - 1)}
          aria-label={`Previous ${itemName}`}
        >
          <ChevronLeft aria-hidden="true" />
        </DimeButton>
        <DimeButton
          type="button"
          variant="outline"
          size="icon-sm"
          disabled={index >= count - 1}
          onClick={() => onIndexChange(index + 1)}
          aria-label={`Next ${itemName}`}
        >
          <ChevronRight aria-hidden="true" />
        </DimeButton>
      </DimeButtonGroup>
    </DimePagination>
  );
}
